import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api-client';
import { login as amplifyLogin, logout as amplifyLogout, getAuthenticatedUser } from '../lib/auth';
import { useAuthStore, isDevMode } from '../stores/auth-store';
import { queryKeys } from '../lib/query-keys';
import type { User } from '../stores/auth-store';

/**
 * useAuth — manages auth state with Amplify + backend sync.
 *
 * On mount: checks if Amplify session exists, fetches /api/me if so.
 * Provides login/logout mutations.
 */
export function useAuth() {
  const { setUser, clearUser, setLoading } = useAuthStore();
  const queryClient = useQueryClient();

  const devLoggedIn = useAuthStore((s) => s.devLoggedIn);

  // Check Amplify session and fetch user profile from backend
  const meQuery = useQuery({
    queryKey: queryKeys.auth.me,
    queryFn: async () => {
      if (isDevMode) {
        // Dev mode: call /api/me directly — X-Dev-User-Id injected by interceptor
        const response = await api.get<{ data: User }>('/me');
        return response.data.data;
      }

      // First check if Amplify has a valid session
      const cognitoUser = await getAuthenticatedUser();
      if (!cognitoUser) {
        return null;
      }

      // Fetch user profile from backend
      const response = await api.get<{ data: User }>('/me');
      return response.data.data;
    },
    // In dev mode, only fetch if user explicitly logged in via dev panel
    enabled: isDevMode ? devLoggedIn : true,
    retry: false,
    staleTime: 60_000, // 1 minute
  });

  // Sync query result to Zustand store
  useEffect(() => {
    if (meQuery.isSuccess) {
      if (meQuery.data) {
        setUser(meQuery.data);
      } else {
        clearUser();
      }
    }
    if (meQuery.isError) {
      clearUser();
    }
    if (!meQuery.isLoading) {
      setLoading(false);
    }
  }, [meQuery.isSuccess, meQuery.isError, meQuery.isLoading, meQuery.data, setUser, clearUser, setLoading]);

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      await amplifyLogin(email, password);
      // Sync user to local DB
      const syncResponse = await api.post<{ data: User }>('/auth/sync');
      return syncResponse.data.data;
    },
    onSuccess: (user) => {
      setUser(user);
      queryClient.setQueryData(queryKeys.auth.me, user);
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: async () => {
      await amplifyLogout();
    },
    onSuccess: () => {
      clearUser();
      queryClient.clear();
    },
  });

  return {
    user: meQuery.data ?? null,
    isLoading: meQuery.isLoading,
    isAuthenticated: !!meQuery.data,
    login: loginMutation.mutateAsync,
    loginError: loginMutation.error,
    isLoggingIn: loginMutation.isPending,
    logout: logoutMutation.mutateAsync,
    isLoggingOut: logoutMutation.isPending,
  };
}

/**
 * useLogout — standalone logout for use in Header dropdown.
 */
export function useLogout() {
  const { clearUser } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await amplifyLogout();
    },
    onSuccess: () => {
      clearUser();
      queryClient.clear();
      navigate('/login', { replace: true });
    },
  });
}
